import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

// schema: [{name,label,type,options,col,placeholder}]  type: text|number|textarea|select|date|email
export default function EntityForm({ open, onOpenChange, title, schema, initial, onSubmit, submitLabel = "Enregistrer" }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = {};
      schema.forEach((f) => { base[f.name] = initial?.[f.name] ?? (f.type === "number" ? 0 : ""); });
      setForm({ ...base, ...(initial || {}) });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {schema.map((f) => (
            <div key={f.name} className={f.col === 2 || f.type === "textarea" ? "md:col-span-2" : ""}>
              <Label className="text-xs">{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`field-${f.name}`} className="mt-1" rows={3} placeholder={f.placeholder} />
              ) : f.type === "select" ? (
                <Select value={String(form[f.name] ?? "")} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`} className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {(f.optionsFn ? f.optionsFn(form) : (f.options || [])).map((o) => {
                      const val = typeof o === "string" ? o : o.value;
                      const lab = typeof o === "string" ? o : o.label;
                      return <SelectItem key={val} value={String(val)}>{lab}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : "text"}
                  value={form[f.name] ?? ""} onChange={(e) => set(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)}
                  data-testid={`field-${f.name}`} className="mt-1" placeholder={f.placeholder} required={f.required} />
              )}
            </div>
          ))}
          <DialogFooter className="md:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="form-submit" className="bg-brand-600 hover:bg-brand-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
